import { useCallback } from 'react';
import { getEventHash, type UnsignedEvent, type Event } from 'nostr-tools';

// ── Client identification tag (NIP-89) ─────────────────────────────
// Full NIP-89 client tag: ["client", name, handler-address, relay-hint]
// The handler address points to the published kind:31990 app descriptor
// so other clients can look up BCHNostr and redirect users here.
const CLIENT_TAG = [
  'client',
  'BCHNostr',
  '31990:df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc:bchstr24-1',
  'wss://relay.bchnostr.com',
];

// Kinds that should carry the client tag — content the user authored.
// Excludes reactions (7), reposts (6), DMs (4/14), deletes (5),
// profile metadata (0), contact lists (3), zap receipts (9735),
// live chat (1311), poll votes (1018), signals (25050), auth (27235),
// and utility addressable events (30078 — boosts, referrals, faucet, etc.).
const CLIENT_TAG_KINDS = new Set([
  1,      // Short text note (posts, replies)
  22,     // Short video (Cash Clips)
  21,     // Video event
  1068,   // Poll
  1111,   // Community post
  30023,  // Long-form article / blog / job listing / merchant listing
  30024,  // Draft article
  30100,  // P2P exchange offer
  30311,  // Live stream
  30312,  // Audio space
  30402,  // Marketplace listing (classifieds)
  34235,  // Addressable video
  34236,  // Addressable short video
  34550,  // Community definition
]);

const DEFAULT_RELAYS = [
  'wss://nos.lol',
  'wss://relay.primal.net',
  // Ditto indexes all tags (not just single-letter), so it improves coverage
  // for our #t discovery queries. Community-endorsed as a healthy relay.
  'wss://relay.ditto.pub',
];

// The app's home relay. Every user must ALWAYS read from and write to this
// relay regardless of their personal relay config, so BCHNostr content is
// consistently discoverable and mentions/notifications are never missed
// just because someone's relay list happens to exclude it.
const CORE_RELAY = 'wss://relay.bchnostr.com';

// Relay URLs to always skip — local network addresses, known-dead relays,
// or anything that causes unnecessary failed connections / AV false positives
const RELAY_BLOCKLIST = [
  'umbrel.local',
  'localhost',
  '127.0.0.1',
  '192.168.',
  '10.0.',
  '10.1.',
  '172.16.',
  'relay.nostr.bg', // frequently down
];

function isRelayAllowed(url: string): boolean {
  return !RELAY_BLOCKLIST.some(blocked => url.includes(blocked));
}

// ── Local event store — persisted in localStorage ─────────────────
// Ensures your own posts show up even if relays lose them
const LOCAL_EVENTS_KEY = 'bchnostr:local-events';
const LOCAL_EVENTS_MAX_AGE = 7 * 86400 * 1000; // 7 days

// Replaceable event kinds — only the newest per author+kind should be kept locally
function isLocalReplaceable(kind: number): boolean {
  return kind === 0 || kind === 3 || (kind >= 10000 && kind < 20000);
}
function isLocalParamReplaceable(kind: number): boolean {
  return kind >= 30000 && kind < 40000;
}

/**
 * Prune replaceable events from the local store: for each author+kind
 * (or author+kind+d for param-replaceable), keep only the newest event.
 * This prevents stale kind 3 (contact list) events from contaminating
 * follow list queries with outdated data.
 */
function pruneReplaceableEvents(events: Event[]): Event[] {
  const replaceableNewest = new Map<string, Event>();
  const result: Event[] = [];

  for (const ev of events) {
    if (isLocalReplaceable(ev.kind)) {
      const key = `${ev.kind}:${ev.pubkey}`;
      const existing = replaceableNewest.get(key);
      if (!existing || ev.created_at > existing.created_at) {
        replaceableNewest.set(key, ev);
      }
    } else if (isLocalParamReplaceable(ev.kind)) {
      const dTag = ev.tags?.find((t: string[]) => t[0] === 'd')?.[1] || '';
      const key = `${ev.kind}:${ev.pubkey}:${dTag}`;
      const existing = replaceableNewest.get(key);
      if (!existing || ev.created_at > existing.created_at) {
        replaceableNewest.set(key, ev);
      }
    } else {
      result.push(ev); // Regular events kept as-is
    }
  }

  // Add only the newest replaceable events
  for (const ev of replaceableNewest.values()) {
    result.push(ev);
  }

  return result;
}

let localEvents: Event[] = (() => {
  try {
    const raw = localStorage.getItem(LOCAL_EVENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Event[];
    // Prune events older than 7 days, then prune replaceable duplicates
    const cutoff = Date.now() / 1000 - 7 * 86400;
    return pruneReplaceableEvents(parsed.filter(e => e.created_at > cutoff));
  } catch { return []; }
})();

function saveLocalEvents() {
  try {
    // Keep last 50 events, max 7 days old, with replaceable dedup
    const cutoff = Date.now() / 1000 - 7 * 86400;
    const pruned = pruneReplaceableEvents(localEvents.filter(e => e.created_at > cutoff));
    const trimmed = pruned.slice(0, 50);
    localStorage.setItem(LOCAL_EVENTS_KEY, JSON.stringify(trimmed));
  } catch {}
}

export function getLocalEvents(): Event[] {
  return localEvents;
}

export function useNostrPublish() {
  const publish = useCallback(async (event: Omit<UnsignedEvent, 'pubkey'>) => {
    if (!(window as any).nostr) {
      throw new Error('No Nostr extension found');
    }
    const pubkey = await (window as any).nostr.getPublicKey();

    // ── Identity guard ────────────────────────────────────────────
    // The signer (window.nostr) and the active identity (current-user) must
    // agree. A browser extension re-injecting window.nostr could otherwise
    // sign as the personal account while the user believes they're acting as
    // a Business Page — silently posting to the wrong profile. The signer
    // lock (lib/nsecSigner.releaseAppSigner) prevents this, but we fail loudly
    // here too rather than ever publish under an unexpected identity.
    const expected = getCurrentPubkey();
    if (expected && pubkey && expected.toLowerCase() !== pubkey.toLowerCase()) {
      const err = new Error(
        'Your signer does not match the active account. Reload the page and switch to the intended account before posting.',
      ) as Error & { code?: string };
      err.code = 'SIGNER_IDENTITY_MISMATCH';
      throw err;
    }

    // Inject client tag on content-creating events so we can count
    // BCHNostr-specific users (not all #bch posters across the network)
    let tags = event.tags || [];
    if (CLIENT_TAG_KINDS.has(event.kind) && !tags.some(t => t[0] === 'client')) {
      tags = [...tags, CLIENT_TAG];
    }

    const unsigned: UnsignedEvent = { ...event, tags, pubkey };
    const id = getEventHash(unsigned as any);
    const signed: Event = await (window as any).nostr.signEvent({ ...unsigned, id });

    // Store locally so it appears in feeds immediately + survives page reload.
    // For replaceable events (kind 0, 3, 10000-19999, 30000-39999), prune
    // stale versions so old kind 3 events don't contaminate follow list queries.
    //
    // For edits ('-edit-of' tag), remove the original from local cache so the
    // feed doesn't show duplicates (original + edit) on the editing user's client.
    // Also remove kind 5 deletion events — they're not displayable content.
    const editOfTag = signed.tags?.find((t: string[]) => t[0] === '-edit-of');
    if (editOfTag?.[1]) {
      localEvents = localEvents.filter(e => e.id !== editOfTag[1]);
    }
    if (signed.kind !== 5) {
      localEvents.unshift(signed);
    }
    localEvents = pruneReplaceableEvents(localEvents);
    if (localEvents.length > 50) localEvents.length = 50;
    saveLocalEvents();

    // Publish to relays.
    // For kind 3 (contact list) and kind 0 (profile), always include
    // relay.bchnostr.com and purplepag.es so we have authoritative copies.
    // For replies (kind 1 with e-tags), also include high-traffic relays
    // so the original post author can always see the comment regardless
    // of which relays the commenter has configured.
    const baseRelays = getRelays();
    const CONTACT_PRIORITY_RELAYS = ['wss://relay.bchnostr.com', 'wss://purplepag.es'];
    const REPLY_PROPAGATION_RELAYS = [
      'wss://relay.bchnostr.com',
      'wss://nos.lol',
      'wss://relay.primal.net',
      'wss://relay.nostr.net',
    ];
    const isReply = signed.kind === 1 && signed.tags?.some((t: string[]) => t[0] === 'e');
    // Any kind-1 note that p-tags other users (a mention, or a reply) MUST
    // reach well-known relays, otherwise the mentioned person's client and
    // the push dispatcher — which read a fixed relay set, not the author's
    // private write relays — never see it. This was the cause of "my friend
    // can't find my post" and "no mention notification": plain posts were
    // published ONLY to the author's own write relays.
    const mentionsOthers =
      signed.kind === 1 &&
      signed.tags?.some((t: string[]) => t[0] === 'p' && typeof t[1] === 'string');
    const needsWideReach = isReply || mentionsOthers;
    // Community posts (kind 1111) and community definitions (kind 34550)
    // must propagate to well-known relays so they're discoverable by everyone
    const COMMUNITY_PROPAGATION_RELAYS = [
      'wss://relay.bchnostr.com',
      'wss://nos.lol',
      'wss://relay.primal.net',
      'wss://relay.nostr.net',
    ];
    const isCommunityEvent = signed.kind === 1111 || signed.kind === 34550 || signed.kind === 4550;
    // Every top-level kind-1 post should at least reach relay.bchnostr.com
    // so it's discoverable in the app's global feed, even if the author's
    // configured write relays are unusual or offline.
    const isTopLevelPost = signed.kind === 1 && !isReply;
    const relays = (event.kind === 3 || event.kind === 0)
      ? [...new Set([...CONTACT_PRIORITY_RELAYS, ...baseRelays])]
      : needsWideReach
        ? [...new Set([...REPLY_PROPAGATION_RELAYS, ...baseRelays])]
        : isCommunityEvent
          ? [...new Set([...COMMUNITY_PROPAGATION_RELAYS, ...baseRelays])]
          : isTopLevelPost
            ? [...new Set(['wss://relay.bchnostr.com', ...baseRelays])]
            : baseRelays;

    // Each relay resolves to a result describing whether it ACCEPTED the
    // event. Previously any relay response (or even a rejection / timeout)
    // was treated as success, so posts that every relay rejected still
    // appeared in the author's own feed (from the local cache) while being
    // invisible to everyone else — "my note link doesn't show up".
    type PublishResult = { url: string; accepted: boolean; reason?: string };

    const promises = relays.map(url => {
      return new Promise<PublishResult>((resolve) => {
        try {
          console.log('[NOSTR] Connecting:', url);

          const ws = new WebSocket(url);
          let finished = false;

          const done = (accepted: boolean, reason?: string) => {
            if (finished) return;
            finished = true;
            try {
              ws.close();
            } catch {}
            resolve({ url, accepted, reason });
          };

          ws.onopen = () => {
            console.log('[NOSTR] Connected:', url);
            try {
              ws.send(JSON.stringify(['EVENT', signed]));
              console.log('[NOSTR] Sent event:', url);
            } catch (err) {
              console.error('[NOSTR] Send failed:', url, err);
              done(false, 'send failed');
            }
          };

          ws.onmessage = (event) => {
            console.log('[NOSTR] Relay response:', url, event.data);

            try {
              const msg = JSON.parse(event.data);

              // NIP-01 OK response: ["OK", <id>, <accepted:boolean>, <message>]
              if (Array.isArray(msg) && msg[0] === 'OK' && msg[1] === signed.id) {
                const accepted = msg[2] === true;
                if (accepted) {
                  console.log('[NOSTR] Accepted by relay:', url);
                } else {
                  console.warn('[NOSTR] REJECTED by relay:', url, msg[3]);
                }
                done(accepted, typeof msg[3] === 'string' ? msg[3] : undefined);
                return;
              }

              // Some relays send NOTICE (informational — not an accept/reject
              // for our specific event). Keep waiting for the OK.
              if (Array.isArray(msg) && msg[0] === 'NOTICE') {
                console.log('[NOSTR] Notice:', url, msg[1]);
                return;
              }
            } catch {}
          };

          ws.onerror = (err) => {
            console.error('[NOSTR] WebSocket failed:', url, err);
            done(false, 'connection error');
          };

          ws.onclose = (ev) => {
            console.log('[NOSTR] Closed:', url, ev.code, ev.reason);
            // If closed before we got an OK, treat as not accepted.
            done(false, 'closed before ack');
          };

          // Safety timeout — no OK received in time.
          setTimeout(() => {
            console.warn('[NOSTR] Timeout:', url);
            done(false, 'timeout');
          }, 10000);

        } catch (err) {
          console.error('[NOSTR] Exception:', url, err);
          resolve({ url, accepted: false, reason: 'exception' });
        }
      });
    });

    const settled = await Promise.allSettled(promises);
    const results: PublishResult[] = settled.map(s =>
      s.status === 'fulfilled' ? s.value : { url: '?', accepted: false, reason: 'rejected' }
    );
    const acceptedCount = results.filter(r => r.accepted).length;

    if (acceptedCount === 0) {
      // No relay accepted the event. It only exists in the local cache, so
      // nobody else can see it. Surface this so the caller can warn/retry
      // instead of silently pretending the post succeeded.
      const reasons = [...new Set(results.map(r => r.reason).filter(Boolean))].join('; ');
      console.error('[NOSTR] Publish FAILED — no relay accepted the event.', reasons);
      const err = new Error(
        `Your post couldn't be saved to any relay${reasons ? ` (${reasons})` : ''}. Please try again.`
      ) as Error & { code?: string };
      err.code = 'NO_RELAY_ACCEPTED';
      throw err;
    }

    console.log(`[NOSTR] Published — accepted by ${acceptedCount}/${results.length} relays.`);
    return signed;
  }, []);

  return { publish };
}

export function getRelays(): string[] {
  try {
    const cfg = JSON.parse(localStorage.getItem('nostr:app-config') || '{}');
    if (cfg.relayMetadata?.relays?.length) {
      const configured = cfg.relayMetadata.relays
        .filter((r: any) => r.write !== false)
        .map((r: any) => r.url)
        .filter(isRelayAllowed);
      // Always write to the home relay too, so every post is discoverable
      // in the app's global feed regardless of the user's write config.
      return [...new Set([CORE_RELAY, ...configured])];
    }
  } catch(e) {}
  return [...new Set([CORE_RELAY, ...DEFAULT_RELAYS])];
}

export function getReadRelays(): string[] {
  try {
    const cfg = JSON.parse(localStorage.getItem('nostr:app-config') || '{}');
    if (cfg.relayMetadata?.relays?.length) {
      const configured = cfg.relayMetadata.relays
        .filter((r: any) => r.read !== false)
        .map((r: any) => r.url)
        .filter(isRelayAllowed);
      // Guarantee the home relay is always read from (deduped), so BCHNostr
      // content published there is never invisible to a user whose relay
      // list happens to omit it. Prepended so it survives the top-N cap.
      return [...new Set([CORE_RELAY, ...configured])];
    }
  } catch(e) {}
  return [...new Set([CORE_RELAY, ...DEFAULT_RELAYS])];
}

/**
 * The logged-in user's pubkey, read synchronously from localStorage.
 *
 * CRITICAL: use this instead of `window.nostr.getPublicKey()` whenever you
 * only need to know WHO the current user is (e.g. to build a query filter).
 * `getPublicKey()` is a signer call — on NIP-07 extensions it's rate-limited
 * and on remote signers (NIP-46 bunkers) it's a network round-trip. Calling
 * it from a hook that mounts once per feed item (as useBookmarks did) fires a
 * storm of signer calls that can lock up the extension / bunker and stall
 * login and content loading for the whole app.
 *
 * The pubkey is written to localStorage at login and is authoritative, so
 * there is never a reason to ask the signer for it just to read it.
 */
export function getCurrentPubkey(): string | null {
  try {
    return localStorage.getItem('bchnostr:current-user');
  } catch {
    return null;
  }
}

